import React, { useState, useEffect } from 'react';
import api from '../../services/api';

// Sorting helpers
const handleSort = (field, currentField, currentDirection, setField, setDirection) => {
  if (currentField === field) {
    setDirection(currentDirection === 'asc' ? 'desc' : 'asc');
  } else {
    setField(field);
    setDirection('asc');
  }
};

const getSortIcon = (field, currentField, currentDirection) => {
  if (currentField !== field) return null;
  return currentDirection === 'asc' ? ' ▲' : ' ▼';
};

const sortData = (data, field, direction) => {
  if (!field) return data;

  return [...data].sort((a, b) => {
    let aVal, bVal;

    if (field === 'created_at') {
      aVal = new Date(a[field]);
      bVal = new Date(b[field]);
    } else if (field === 'prompt_count' || field === 'reflection_count') {
      aVal = parseInt(a[field]) || 0;
      bVal = parseInt(b[field]) || 0;
    } else {
      aVal = a[field];
      bVal = b[field];
    }

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (aVal instanceof Date && bVal instanceof Date) {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const stringA = String(aVal).toLowerCase();
    const stringB = String(bVal).toLowerCase();
    const comparison = stringA.localeCompare(stringB);
    return direction === 'asc' ? comparison : -comparison;
  });
};

function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    slug: '',
    icon: '',
    iconUrl: '',
    displayOrder: 0,
    exportName: ''
  });
  const [pendingIconFile, setPendingIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Failed to fetch categories: ${errorMsg}`);
    }
  };

  const openAddCategoryModal = () => {
    setCategoryFormData({
      name: '',
      slug: '',
      icon: '',
      displayOrder: categories.length,
      exportName: ''
    });
    setIsAddingCategory(true);
  };

  const openEditCategoryModal = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      slug: category.slug,
      icon: category.icon || '',
      iconUrl: category.icon_url || '',
      displayOrder: category.display_order,
      exportName: category.export_name || ''
    });
    setPendingIconFile(null);
    setIconPreviewUrl(category.icon_url || null);
  };

  const closeCategoryModal = () => {
    setEditingCategory(null);
    setIsAddingCategory(false);
    setCategoryFormData({
      name: '',
      slug: '',
      icon: '',
      iconUrl: '',
      displayOrder: 0,
      exportName: ''
    });
    setPendingIconFile(null);
    setIconPreviewUrl(null);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    try {
      let categoryId;

      if (isAddingCategory) {
        const response = await api.post('/admin/categories', categoryFormData);
        categoryId = response.data.id;
      } else {
        await api.put(`/admin/categories/${editingCategory.id}`, categoryFormData);
        categoryId = editingCategory.id;
      }

      if (pendingIconFile && categoryId) {
        const formData = new FormData();
        formData.append('icon', pendingIconFile);
        await api.post(`/admin/categories/${categoryId}/icon`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      alert(isAddingCategory ? 'Category created successfully' : 'Category updated successfully');
      fetchCategories();
      closeCategoryModal();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPendingIconFile(file);
      const previewUrl = URL.createObjectURL(file);
      setIconPreviewUrl(previewUrl);
    }
  };

  const handleRemoveIcon = async () => {
    if (editingCategory && categoryFormData.iconUrl) {
      try {
        await api.delete(`/admin/categories/${editingCategory.id}/icon`);
        setCategoryFormData({ ...categoryFormData, iconUrl: '' });
        setIconPreviewUrl(null);
        fetchCategories();
      } catch (error) {
        console.error('Error removing icon:', error);
        alert('Failed to remove icon: ' + (error.response?.data?.error || error.message));
      }
    } else {
      setPendingIconFile(null);
      setIconPreviewUrl(null);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete category "${categoryName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/categories/${categoryId}`);
      fetchCategories();
      alert('Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <>
      <div className="admin-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Category Management</h2>
          <button
            className="btn-edit"
            onClick={openAddCategoryModal}
            style={{ marginBottom: 0 }}
          >
            + Add Category
          </button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('name', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Name{getSortIcon('name', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('slug', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Slug{getSortIcon('slug', sortField, sortDirection)}
              </th>
              <th>Icon</th>
              <th
                onClick={() => handleSort('display_order', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Display Order{getSortIcon('display_order', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('prompt_count', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Prompts{getSortIcon('prompt_count', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('reflection_count', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Reflections{getSortIcon('reflection_count', sortField, sortDirection)}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortData(categories, sortField, sortDirection).map(category => (
              <tr key={category.id}>
                <td>{category.name}</td>
                <td><code>{category.slug}</code></td>
                <td style={{ textAlign: 'center' }}>
                  {category.icon_url ? (
                    <img
                      src={category.icon_url}
                      alt={`${category.name} icon`}
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : category.icon ? (
                    <span style={{ fontSize: '1.5rem' }}>{category.icon}</span>
                  ) : '-'}
                </td>
                <td>{category.display_order}</td>
                <td>{category.prompt_count}</td>
                <td>{category.reflection_count}</td>
                <td>
                  <button
                    className="btn-edit"
                    onClick={() => openEditCategoryModal(category)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteCategory(category.id, category.name)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editingCategory || isAddingCategory) && (
        <div className="modal-overlay">
          <div className="modal-content admin-modal">
            <div className="modal-header">
              <h3>{isAddingCategory ? 'Add New Category' : `Edit Category: ${editingCategory.name}`}</h3>
              <button onClick={closeCategoryModal}>✕</button>
            </div>

            <form onSubmit={handleSaveCategory} className="admin-form">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  required
                  placeholder="e.g., Early Childhood"
                />
              </div>

              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  value={categoryFormData.slug}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, slug: e.target.value })}
                  required
                  placeholder="e.g., early-childhood (lowercase, no spaces)"
                  pattern="[a-z0-9-]+"
                  title="Only lowercase letters, numbers, and hyphens allowed"
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Unique identifier used in URLs (lowercase, no spaces)
                </small>
              </div>

              <div className="form-group">
                <label>Icon Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {iconPreviewUrl ? (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={iconPreviewUrl}
                        alt="Category icon"
                        style={{
                          width: '64px',
                          height: '64px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveIcon}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          border: 'none',
                          background: '#ef4444',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Remove icon"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '8px',
                        border: '2px dashed #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                        fontSize: '24px'
                      }}
                    >
                      ?
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      id="category-icon-upload"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      onChange={handleIconFileChange}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="category-icon-upload"
                      style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        background: '#f3f4f6',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151'
                      }}
                    >
                      {iconPreviewUrl ? 'Change Image' : 'Upload Image'}
                    </label>
                    <small style={{ display: 'block', color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      JPEG, PNG, GIF, WebP, or SVG (max 2MB)
                    </small>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={categoryFormData.displayOrder}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, displayOrder: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="0"
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Lower numbers appear first in the list
                </small>
              </div>

              <div className="form-group">
                <label>Export Name</label>
                <input
                  type="text"
                  value={categoryFormData.exportName}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, exportName: e.target.value })}
                  placeholder="e.g., family-memories"
                  maxLength="100"
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Used in exported media file naming. Falls back to category-{'{id}'} if blank.
                </small>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-save">
                  {isAddingCategory ? 'Create Category' : 'Save Changes'}
                </button>
                <button type="button" onClick={closeCategoryModal} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminCategories;
